<script setup lang="ts">
import { useConfirm, VIcons } from '@/shared/ui'
import { WObjectSubTypeSelect } from '@/widgets'
import { InventObjectTypesEdgesButton } from '@/features/Inventory/ObjectTypesEdges'
import InventObjectTypesEdgesModal from '@/features/Inventory/ObjectTypesEdges/ui/InventObjectTypesEdgesModal.vue'
import { useModel } from './../model/model.ts'
import { useRoute } from 'vue-router'
import { computed } from 'vue'
import type { ObjectTypesEdgesType } from './../model/types.ts'
import { CardinalityOptions } from './../model/types.ts'
import { useHelpers, useTranslate } from '@/shared/lib'
import { useRemoveObjectTepesEdges } from '@/features/Inventory/ObjectTypesEdgesDelete'
import { useObjectTypesEdgesCreate } from '@/features/Inventory/ObjectTypesEdgesCreate'
import { useObjectTypesEdgesUpdate } from '@/features/Inventory/ObjectTypesEdgesUpdate'

const route = useRoute()
const { show } = useConfirm()
const { trs } = useTranslate()
const { getDifferentObject } = useHelpers()

const { data, oldData, loadData } = useModel()
const { remove } = useRemoveObjectTepesEdges()
const { create } = useObjectTypesEdgesCreate()
const { update } = useObjectTypesEdgesUpdate()

const fromId = computed(() => route.params.entity_id as string)

const { isPending, refetch } = loadData(fromId)

/**
 * Функция добавления родителя в список
 */
const addHandler = () => {
  data.value.push({
    name: '',
    from_type_id: Number(fromId.value),
    to_type_id: 26278,
    table_fields: [],
    sum_fields: [],
    cardinality: '',
    field_a_id: null,
    field_b_id: null,
  })
}

/**
 * Удаление элементов связанности
 * @param index
 * @param item
 */
const removeHandler = (index: number, item: ObjectTypesEdgesType) => {
  if (!item.id) {
    data.value.splice(index, 1)
    return
  }

  show({
    title: trs('itf.relation_remove_confirm'),
    confirmFunc: () => remove(item).then(() => refetch()),
  })
}

/**
 * Функция обновления/сохранения связи
 * @param item
 */
const upsertHandler = (item: ObjectTypesEdgesType) => {
  if (item.id) {
    const oldValue = oldData.value[item.id]
    const diff = getDifferentObject(oldValue, item)

    update({
      id: item.id,
      from_type_id: item.from_type_id,
      diff,
    }).then(() => refetch())
  } else {
    create(item).then(() => refetch())
  }
}
</script>

<template>
  <VTable class="invent-object-types-fields__table table_hover" :class="{ disabled: isPending }">
    <thead>
      <tr>
        <th>{{ $t('itf.name_label') }}</th>
        <th>{{ $t('itf.type') }}</th>
        <th>Тип связи</th>
        <th>{{ $t('itf.action') }}</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(item, index) in data" :key="index">
        <td>
          <VTextField
            :label="$t('itf.name_label')"
            hide-details
            v-model="item.name"
            @focusout="upsertHandler(item)"
          />
        </td>
        <td>
          <WObjectSubTypeSelect
            :label="$t('itf.type')"
            hide-details
            v-model="item.to_type_id"
            @change="upsertHandler(item)"
          />
        </td>
        <td>
          <VSelect
            label="Тип связи"
            hide-details
            :items="CardinalityOptions"
            v-model="item.cardinality"
            @update:model-value="upsertHandler(item)"
          />
        </td>
        <td>
          <div class="table__btn_left">
            <VBtn icon density="compact" color="error" @click="removeHandler(index, item)">
              <VIcons type="delete" />
            </VBtn>

            <InventObjectTypesEdgesButton :item="item" />
          </div>
        </td>
      </tr>
    </tbody>
  </VTable>

  <VBtn class="mt-4" color="default" variant="tonal" @click.prevent="addHandler">
    {{ $t('itf.add') }}
  </VBtn>

  <InventObjectTypesEdgesModal @save="upsertHandler" />
</template>
